import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Shield, Lock, AlertTriangle, CheckCircle, Eye, Download, Ban, RefreshCw, Activity, Users, Clock } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

const SecurityPanel = ({ authFetch }) => {
  const [dashboard, setDashboard] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [blockedIps, setBlockedIps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedDays, setSelectedDays] = useState(7);

  const fetchSecurityData = async () => {
    setLoading(true);
    try {
      const [dashRes, logsRes, alertsRes, ipsRes] = await Promise.all([
        authFetch(`/api/security/dashboard?days=${selectedDays}`),
        authFetch(`/api/security/audit-logs?days=${selectedDays}&limit=50`),
        authFetch('/api/security/alerts?hours=24'),
        authFetch('/api/security/blocked-ips')
      ]);

      if (dashRes.ok) setDashboard(await dashRes.json());
      if (logsRes.ok) setAuditLogs((await logsRes.json()).logs || []);
      if (alertsRes.ok) setAlerts((await alertsRes.json()).alerts || []);
      if (ipsRes.ok) setBlockedIps(Object.entries((await ipsRes.json()).blocked_ips || {}));
    } catch (error) {
      console.error('Failed to fetch security data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSecurityData();
  }, [selectedDays]);

  const handleUnblockIp = async (ip) => {
    if (!window.confirm(`Are you sure you want to unblock IP ${ip}?`)) return;
    try {
      const res = await authFetch(`/api/security/unblock-ip/${ip}`, { method: 'POST' });
      if (res.ok) {
        setBlockedIps(prev => prev.filter(([blockedIp]) => blockedIp !== ip));
      }
    } catch (error) {
      console.error('Failed to unblock IP:', error);
    }
  };

  const getActionBadge = (action) => {
    const badges = {
      login: { variant: 'default', icon: CheckCircle, label: 'Login' },
      login_failed: { variant: 'destructive', icon: AlertTriangle, label: 'Failed Login' },
      create: { variant: 'secondary', icon: Activity, label: 'Create' },
      update: { variant: 'secondary', icon: Activity, label: 'Update' },
      delete: { variant: 'destructive', icon: AlertTriangle, label: 'Delete' },
      pii_access: { variant: 'outline', icon: Eye, label: 'PII Access' },
      export: { variant: 'outline', icon: Download, label: 'Export' }
    };
    const badge = badges[action] || { variant: 'secondary', icon: Activity, label: action };
    const Icon = badge.icon;
    return (
      <Badge variant={badge.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {badge.label}
      </Badge>
    );
  };

  const formatTimestamp = (ts) => {
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts;
    }
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-green-600" />
            Security Dashboard
          </h2>
          <p className="text-sm text-gray-500 mt-1">Monitor authentication, data access, and security alerts</p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={selectedDays}
            onChange={(e) => setSelectedDays(Number(e.target.value))}
            className="border rounded px-3 py-1.5 text-sm"
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <Button variant="outline" size="sm" onClick={fetchSecurityData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Security Alerts */}
      {alerts.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>{alerts.length} Security Alert{alerts.length > 1 ? 's' : ''}</strong>
            <ul className="mt-2 list-disc list-inside">
              {alerts.map((alert, i) => (
                <li key={i}>{alert.message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Events</p>
                  <p className="text-2xl font-bold">{dashboard.metrics.total_events}</p>
                </div>
                <Activity className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Successful Logins</p>
                  <p className="text-2xl font-bold text-green-600">{dashboard.metrics.successful_logins}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Failed Logins</p>
                  <p className="text-2xl font-bold text-red-600">{dashboard.metrics.failed_logins}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Blocked IPs</p>
                  <p className="text-2xl font-bold text-orange-600">{dashboard.metrics.blocked_ips}</p>
                </div>
                <Ban className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-4">
          {['dashboard', 'audit-logs', 'blocked-ips'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-2 px-1 border-b-2 transition-colors ${
                activeTab === tab 
                  ? 'border-green-600 text-green-600 font-medium' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'dashboard' && 'Overview'}
              {tab === 'audit-logs' && 'Audit Logs'}
              {tab === 'blocked-ips' && 'Blocked IPs'}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && dashboard && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Security Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`p-4 rounded-lg ${
                dashboard.security_status === 'healthy' 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center gap-2">
                  {dashboard.security_status === 'healthy' ? (
                    <>
                      <CheckCircle className="h-6 w-6 text-green-600" />
                      <span className="font-medium text-green-700">System Healthy</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-6 w-6 text-red-600" />
                      <span className="font-medium text-red-700">Attention Needed</span>
                    </>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  {dashboard.security_status === 'healthy' 
                    ? 'No unusual activity detected in the monitored period.'
                    : 'Review recent security alerts and audit logs.'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Action Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Action Breakdown</CardTitle>
              <CardDescription>Activity distribution over {selectedDays} days</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.entries(dashboard.action_breakdown).length === 0 ? (
                <p className="text-gray-500 text-sm">No activity recorded</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(dashboard.action_breakdown).map(([action, count]) => (
                    <div key={action} className="flex items-center justify-between">
                      {getActionBadge(action)}
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'audit-logs' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Audit Logs
            </CardTitle>
            <CardDescription>Detailed record of all security-relevant events</CardDescription>
          </CardHeader>
          <CardContent>
            {auditLogs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No audit logs found for the selected period</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 font-medium">Timestamp</th>
                      <th className="pb-2 font-medium">Action</th>
                      <th className="pb-2 font-medium">User</th>
                      <th className="pb-2 font-medium">IP Address</th>
                      <th className="pb-2 font-medium">Status</th>
                      <th className="pb-2 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log, i) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-2 text-gray-600 whitespace-nowrap">
                          {formatTimestamp(log.timestamp)}
                        </td>
                        <td className="py-2">{getActionBadge(log.action)}</td>
                        <td className="py-2 text-gray-700">{log.user_email_masked || log.user_id || '-'}</td>
                        <td className="py-2 font-mono text-xs text-gray-500">{log.ip_address || '-'}</td>
                        <td className="py-2">
                          {log.success ? (
                            <Badge variant="outline" className="text-green-600 border-green-600">Success</Badge>
                          ) : (
                            <Badge variant="destructive">Failed</Badge>
                          )}
                        </td>
                        <td className="py-2 text-gray-500 text-xs max-w-[200px] truncate">
                          {log.error_message || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'blocked-ips' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-red-500" />
              Blocked IP Addresses
            </CardTitle>
            <CardDescription>IPs blocked due to too many failed authentication attempts</CardDescription>
          </CardHeader>
          <CardContent>
            {blockedIps.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-600">No IP addresses are currently blocked</p>
              </div>
            ) : (
              <div className="space-y-2">
                {blockedIps.map(([ip, expiresAt]) => (
                  <div key={ip} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                    <div>
                      <span className="font-mono font-medium">{ip}</span>
                      <p className="text-xs text-gray-500">Blocked until: {formatTimestamp(expiresAt)}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleUnblockIp(ip)}>
                      Unblock
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Security Tips */}
      <Card className="bg-gradient-to-r from-gray-50 to-gray-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Lock className="h-5 w-5" />
            Security Features Active
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Rate Limiting</p>
                <p className="text-xs text-gray-500">10 login attempts/min before lockout</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">IP Blocking</p>
                <p className="text-xs text-gray-500">Auto-block after 10 failed attempts</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Audit Logging</p>
                <p className="text-xs text-gray-500">All security events tracked</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityPanel;
